import {
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
} from '@xyflow/react';
import { makeVisualEdgeData } from '../schema/defaults';

export type Curvature = 'straight' | 'smooth' | 'step';
export type StrokeStyle = 'solid' | 'dashed' | 'dotted';
export type Marker = 'none' | 'arrow' | 'dot';

export interface EdgeVisualData extends Record<string, unknown> {
  label: string;
  stroke: string;
  strokeWidth: 1 | 2 | 4;
  strokeStyle: StrokeStyle;
  markerStart: Marker;
  markerEnd: Marker;
  curvature: Curvature;
  /** Optional, step edges only: how far the path runs out of a handle before
   *  turning (getSmoothStepPath's offset; 20 when absent). Synapse sets it on
   *  a process flow's loop edges so nested loops run on separate lanes. */
  stepOffset?: number;
}

/** New-edge defaults — the same payload createEdge.ts seeds onto fresh
 *  edges. Centralised so the rendered edge, the in-flight connection
 *  line, and the persisted Y.Map agree on the starting visual. */
export const DEFAULT_EDGE_DATA: EdgeVisualData = makeVisualEdgeData() as EdgeVisualData;

export function dashArrayFor(style: StrokeStyle, width: number): string | undefined {
  if (style === 'solid') return undefined;
  if (style === 'dashed') return `${width * 4} ${width * 3}`;
  return `${width} ${width * 2}`;
}

export function pathFor(
  curvature: Curvature,
  args: Parameters<typeof getBezierPath>[0],
  stepOffset?: number,
): [string, number, number] {
  if (curvature === 'straight') {
    const [d, lx, ly] = getStraightPath(args);
    return [d, lx, ly];
  }
  if (curvature === 'step') {
    const offset = typeof stepOffset === 'number' && Number.isFinite(stepOffset) && stepOffset > 0 ? stepOffset : undefined;
    const [d, lx, ly] = getSmoothStepPath({ ...args, offset });
    return [d, lx, ly];
  }
  const [d, lx, ly] = getBezierPath(args);
  return [d, lx, ly];
}
